import { useCallback, useState } from 'react';
import { useToast } from '@/shared/widgets/toast';
import type { CustomizationService } from '../../application/services/CustomizationService';
import type { CustomizationSelection } from '../../domain/models/CustomizationSelection';
import type { ProductCustomization } from '../../domain/models/ProductCustomization';
import type { SizeOption } from '../../utils/parseSizeList';
import { findSizeOption } from '../../utils/parseSizeList';
import { metalToSelectionColor } from '../../utils/parseCustomizationOptions';

export interface SizeSelectionContext {
  designId: string;
  metal: string;
  goldPurity: string | null;
  diamondStoneType: string | null;
  solitaireCarat: number | null;
  solitaireClarity: string | null;
  solitaireColor: string | null;
  customizationId: number;
  sizes: SizeOption[];
}

export interface UseSizeSelectionOptions {
  service: CustomizationService;
  context: SizeSelectionContext | null;
  selectedSize: SizeOption | null;
  isSizeable: boolean;
  readyToShipQuantity: number | null;
  onApplied: (result: {
    customization: ProductCustomization;
    selectedSize: SizeOption | null;
  }) => void;
}

export interface UseSizeSelectionReturn {
  isApplying: boolean;
  sizeNotSelected: boolean;
  shakeKey: number;
  handleSizeSelect: (size: SizeOption) => Promise<void>;
  validateSizeSelection: () => boolean;
}

function buildSizeSelectionParams(
  context: SizeSelectionContext,
  sizeKey: string
): CustomizationSelection {
  return {
    customization_id: context.customizationId,
    metal_color: metalToSelectionColor(context.metal),
    metal_karatage: context.goldPurity ?? undefined,
    diamond_type: context.diamondStoneType ?? undefined,
    solitaire_carat: context.solitaireCarat ?? undefined,
    solitaire_clarity: context.solitaireClarity ?? undefined,
    solitaire_color: context.solitaireColor ?? undefined,
    solitaire_quality:
      context.solitaireClarity && context.solitaireColor
        ? `${context.solitaireClarity}_${context.solitaireColor}`
        : undefined,
    size: sizeKey,
  };
}

function getCustomizationErrorMessage(error: unknown): string {
  if (error && typeof error === 'object') {
    const err = error as { data?: { message?: string }; message?: string };
    if (typeof err.data?.message === 'string' && err.data.message) return err.data.message;
    if (typeof err.message === 'string' && err.message) return err.message;
  }
  return 'Unable to update size. Please try again.';
}

export function useSizeSelection({
  service,
  context,
  selectedSize,
  isSizeable,
  readyToShipQuantity: _readyToShipQuantity,
  onApplied,
}: UseSizeSelectionOptions): UseSizeSelectionReturn {
  const { toast } = useToast();
  const [isApplying, setIsApplying] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  const [shakeKey, setShakeKey] = useState(0);

  // Size selection is required for all sizeable products (mirrors old-react behaviour).
  // readyToShipQuantity only affects whether submission is blocked — not whether the
  // animation/error shows — so we keep it out of this check.
  const requiresSizeSelection = isSizeable;
  const sizeNotSelected = isSizeable && !selectedSize && showValidationError;

  const handleSizeSelect = useCallback(
    async (size: SizeOption) => {
      if (!context) return;
      setShowValidationError(false);
      setIsApplying(true);
      try {
        const params = buildSizeSelectionParams(context, size.key);
        const result = await service.applyCustomization(context.designId, params);
        const matched = findSizeOption(context.sizes, size.key) ?? size;
        onApplied({ customization: result, selectedSize: matched });
      } catch (error) {
        toast({
          title: 'Size unavailable',
          description: getCustomizationErrorMessage(error),
          variant: 'destructive',
          duration: 3000,
        });
      } finally {
        setIsApplying(false);
      }
    },
    [context, onApplied, service, toast]
  );

  const validateSizeSelection = useCallback(() => {
    if (!requiresSizeSelection || selectedSize) {
      setShowValidationError(false);
      return true;
    }
    setShowValidationError(true);
    setShakeKey((key) => key + 1);
    return false;
  }, [requiresSizeSelection, selectedSize]);

  return {
    isApplying,
    sizeNotSelected,
    shakeKey,
    handleSizeSelect,
    validateSizeSelection,
  };
}
